import { useEffect, useRef, useState } from 'react'
import cn from 'classnames'
import { MultiSelect, Select } from '@forwardflow/ui-v2'
import {
   CAMPAIGN_TYPE_OPTIONS,
   EVENT_OPTIONS,
   ICONS_MAP,
   PRIMARY_FILTER_OPTIONS,
} from '@forwardflow/features/campaign-filter/constants'
import { FilterRow } from '@forwardflow/features/campaign-filter/components/FilterRow'
import { FilterOptionsContext } from '@forwardflow/features/campaign-filter/context/FilterOptions.context'
import { useFilterOptionsSource } from '@forwardflow/features/campaign-flow/hooks/useFilterOptionsSource'
import {
   StepFooter,
   StepHeading,
} from '@forwardflow/features/campaign-flow/components/frame/FlowFrame'
import { FlowIcon } from '@forwardflow/features/campaign-flow/components/ui/FlowIcon'
import {
   Checkbox,
   Spinner,
} from '@forwardflow/features/campaign-flow/components/ui/primitives'
import {
   IActivityTrigger,
   IAudience,
   IFilterRow,
   TCampaignKind,
} from '@forwardflow/features/campaign-flow/interfaces/campaign-flow.types'
import { campaignFlowService } from '@forwardflow/features/campaign-flow/services'
import { errorText } from '@forwardflow/features/campaign-flow/utils/errors'
import { useCampaignFlowStore } from '@forwardflow/features/campaign-flow/store/flow.store'
import {
   autoName,
   KINDS,
   LEAD_TYPE_LABEL,
   LEAD_TYPES,
   filterVocabularyNow,
   setFilterVocabulary,
} from '@forwardflow/features/campaign-flow/utils/describe'

type TField = IFilterRow['field']
/** A section the user can open from "Add a filter manually". */
type TSection = 'types' | 'activity' | TField

/** What each type means, in the words the screen uses. */
const KIND_BLURB: Record<TCampaignKind, string> = {
   'new-lead':
      'Leads under 30 days old, plus every new lead that comes in from now on.',
   revive: 'Leads older than 30 days that went quiet.',
   activity:
      'Called right after a lead does something, like viewing or saving a home.',
}
const ONE_TYPE =
   'One type per campaign. Want new and cold leads? Create a campaign for each: precise control over scripts and monitoring.'

/** Live count: re-estimates 250 ms after the last change. */
const useLiveCount = (
   audience: IAudience,
   onCount: (n: number) => void,
   onError: (message: string | null) => void
) => {
   const [counting, setCounting] = useState(false)
   const key = JSON.stringify([
      audience.kinds,
      audience.leadTypes,
      audience.filters,
   ])
   useEffect(() => {
      let live = true
      if (!audience.kinds.length) return undefined
      setCounting(true)
      onError(null)
      const t = window.setTimeout(() => {
         void campaignFlowService
            .estimateLeads(audience)
            .then((n) => {
               if (!live) return
               setCounting(false)
               onCount(n)
            })
            .catch((e: unknown) => {
               if (!live) return
               setCounting(false)
               onError(errorText(e, 'The leads could not be counted.'))
            })
      }, 250)
      return () => {
         live = false
         window.clearTimeout(t)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [key])
   return counting
}

/**
 * Question 1. Starts bare: a campaign type, a sentence box and "Add a filter
 * manually". Filters appear one section at a time, and the name field with
 * its suggestion appears once something has been chosen.
 */
export const WhoStep = ({
   onDone,
   busy,
}: {
   onDone: () => void
   busy: boolean
}) => {
   const { draft, update } = useCampaignFlowStore()
   const a = draft.audience
   const [text, setText] = useState(a.description)
   const [reading, setReading] = useState(false)
   /* What the description box understood, and the words it could not place. */
   const [heard, setHeard] = useState<{
      understood: string[]
      unknown: string[]
      /** The sentence named no type, or several: ask which one. */
      ask: TCampaignKind[] | null
   } | null>(null)
   /* Sections opened by hand (ones with values are open by their values). */
   const [opened, setOpened] = useState<TSection[]>([])
   const optionsSource = useFilterOptionsSource()
   const nameTouched = useRef(!!a.name && a.name !== autoName(a))

   const setAudience = (fn: (x: IAudience) => IAudience) =>
      update((d) => {
         const next = fn(d.audience)
         return {
            ...d,
            audience: nameTouched.current
               ? next
               : { ...next, name: autoName(next) },
         }
      })
   /* The organization's own sources, stages, tags and ponds; the picker re-renders once they arrive. */
   const [, setVocabLoaded] = useState(false)
   useEffect(() => {
      let alive = true
      void campaignFlowService
         .filterVocabulary()
         .then((v) => {
            setFilterVocabulary(v)
            if (alive) setVocabLoaded(true)
         })
         .catch(() => undefined)
      return () => {
         alive = false
      }
   }, [])
   const [countError, setCountError] = useState<string | null>(null)
   const counting = useLiveCount(
      a,
      (n) =>
         update((d) => ({ ...d, audience: { ...d.audience, leadCount: n } })),
      setCountError
   )
   /*
    * Where these leads live is counted while the user reads the count, so
    * the numbers are ready at Continue (the service keeps it for a minute).
    */
   const localityKey = JSON.stringify([
      a.kinds,
      a.leadTypes,
      a.filters,
      a.activity,
   ])
   useEffect(() => {
      if (!a.kinds.length) return undefined
      const t = window.setTimeout(
         () => void campaignFlowService.areaCodes(a).catch(() => undefined),
         3000
      )
      return () => window.clearTimeout(t)
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [localityKey])

   const read = async () => {
      if (!text.trim()) return
      setReading(true)
      const { understood, unknown, kindsMentioned, ...parsed } =
         await campaignFlowService.interpretDescription(text)
      /* A type already chosen on the toggle stays unless the sentence names one. */
      setAudience((x) => ({
         ...parsed,
         kinds: parsed.kinds.length ? parsed.kinds : x.kinds,
         leadCount: x.leadCount,
      }))
      const chosen = parsed.kinds.length || a.kinds.length
      setHeard({
         understood,
         unknown,
         ask: !chosen || kindsMentioned.length > 1 ? kindsMentioned : null,
      })
      setReading(false)
      /* The sentence filled the form below the fold: bring the name (and Continue) into view. */
      window.setTimeout(() => {
         document
            .querySelector('[data-testid="cf-name"]')
            ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }, 120)
   }
   const toggle = <T,>(list: T[], v: T) =>
      list.includes(v) ? list.filter((x) => x !== v) : [...list, v]
   const count = a.leadCount ?? 0
   const kind = a.kinds[0]
   const activity: IActivityTrigger = a.activity ?? {
      events: ['all'],
      lookbackDays: null,
      pondIds: [],
   }
   const pickKind = (k: TCampaignKind) => {
      setHeard((h) => (h ? { ...h, ask: null } : h))
      setAudience((x) => ({
         ...x,
         kinds: [k],
         /* Activity campaigns start on every event until the user narrows it. */
         activity: k === 'activity' ? (x.activity ?? activity) : x.activity,
      }))
   }
   const setActivity = (patch: Partial<IActivityTrigger>) =>
      setAudience((x) => ({
         ...x,
         activity: { ...activity, ...x.activity, ...patch },
      }))

   /* Which sections show: opened by hand, or carrying a value. */
   const usedFields = new Set<TField>(a.filters.map((f) => f.field))
   const showTypes = opened.includes('types') || a.leadTypes.length > 0
   const showActivity = kind === 'activity'
   const started =
      !!heard ||
      showTypes ||
      a.filters.length > 0 ||
      !!a.activity ||
      nameTouched.current
   const openSection = (s: TSection) => {
      if (s === 'types' || s === 'activity') return setOpened((o) => [...o, s])
      if (usedFields.has(s)) return
      setAudience((x) => ({
         ...x,
         filters: [
            ...x.filters,
            { field: s, operator: 'CONTAINS_ANY', logic: 'AND', value: [] },
         ],
      }))
   }
   const menu: Array<{ key: TSection; label: string }> = [
      ...(!showTypes
         ? [{ key: 'types' as const, label: 'Looking to buy, sell or rent' }]
         : []),
      ...PRIMARY_FILTER_OPTIONS.filter(
         (o) => !usedFields.has(o.value as TField)
      ).map((o) => ({ key: o.value as TField, label: o.label })),
   ]
   const suggested = autoName(a)
   const missing = !kind ? 'Pick a campaign type' : null

   const chip = (on: boolean) =>
      cn(
         'rounded-lg border px-3 py-2 flex items-center gap-2.5 text-left transition-colors',
         on
            ? 'bg-[#A684FF]/10 border-[#A684FF]/40'
            : 'bg-white/[0.02] border-white/[0.1] hover:bg-white/[0.05]'
      )
   const filterRows = a.filters.map((row, i) => (
      <div key={`${row.field}-${i}`} data-testid={`cf-filter-${i}`}>
         <FilterRow
            filterCriteria={row}
            usedFields={usedFields}
            onUpdate={(r) =>
               setAudience((x) => ({
                  ...x,
                  filters: x.filters.map((f, j) => (j === i ? r : f)),
               }))
            }
            onRemove={() =>
               setAudience((x) => ({
                  ...x,
                  filters: x.filters.filter((_, j) => j !== i),
               }))
            }
         />
      </div>
   ))

   return (
      <>
         <div className="px-5 md:px-8 pt-6 pb-4 flex flex-col gap-5 max-w-[780px] w-full mx-auto">
            <StepHeading
               align="left"
               icon="users"
               title="Who should we call?"
               lede="Pick a campaign type, then say it in your own words or add filters."
               right={
                  <div className="text-right shrink-0" data-testid="cf-count">
                     <div className="text-[28px] leading-8 font-semibold">
                        {!kind
                           ? '—'
                           : counting && a.leadCount === null
                             ? '…'
                             : count.toLocaleString()}
                     </div>
                     {countError && (
                        <div
                           className="text-xs text-amber-300 max-w-[280px] text-right"
                           data-testid="cf-count-error"
                        >
                           Could not count: {countError}
                        </div>
                     )}
                     <div className="text-xs text-[#9ca3af] flex items-center justify-end gap-1.5">
                        <span
                           className={cn(
                              'w-1.5 h-1.5 rounded-full',
                              counting
                                 ? 'bg-amber-400'
                                 : countError
                                   ? 'bg-amber-300'
                                   : 'bg-[#00d857]'
                           )}
                        />
                        {!kind
                           ? 'pick a type to count'
                           : counting
                             ? 'Counting'
                             : 'leads match'}
                     </div>
                  </div>
               }
            />

            {/* One campaign type at a time */}
            <div className="flex flex-col gap-2">
               <div
                  className="grid grid-cols-3 gap-1 bg-white/[0.04] border border-white/[0.06] rounded-xl p-1"
                  role="radiogroup"
                  data-testid="cf-kinds"
               >
                  {KINDS.map((k) => {
                     const opt = CAMPAIGN_TYPE_OPTIONS.find((o) => o.id === k)
                     const on = kind === k
                     return (
                        <button
                           key={k}
                           type="button"
                           role="radio"
                           aria-checked={on}
                           aria-pressed={on}
                           onClick={() => pickKind(k)}
                           className={cn(
                              'h-11 rounded-lg flex items-center justify-center gap-2 text-sm font-semibold transition-colors',
                              on
                                 ? 'bg-[#9061DA] text-white'
                                 : 'text-[#9ca3af] hover:text-white hover:bg-white/[0.05]'
                           )}
                           data-testid={`cf-kind-${k}`}
                        >
                           {opt?.label}
                           <span className="scale-[.7] origin-left -ml-1 hidden sm:inline">
                              {opt?.icon}
                           </span>
                        </button>
                     )
                  })}
               </div>
               <p className="text-xs text-[#9ca3af] px-1">
                  {kind ? KIND_BLURB[kind] : ONE_TYPE}
               </p>
            </div>

            <form
               className="cf-glow"
               onSubmit={(e) => {
                  e.preventDefault()
                  void read()
               }}
            >
               <div className="h-[52px] rounded-full bg-[#202127] flex items-center pl-5 pr-1.5 gap-2">
                  <FlowIcon
                     name="spark"
                     size={16}
                     className="text-[#DAC2FF] shrink-0"
                  />
                  <input
                     value={text}
                     onChange={(e) => setText(e.target.value)}
                     placeholder="e.g. new leads from Zillow, cold leads from Facebook, or leads who saved a home"
                     className="flex-1 min-w-0 bg-transparent outline-none text-[15px] placeholder:text-white/35"
                     data-testid="cf-describe-input"
                  />
                  <button
                     type="submit"
                     disabled={!text.trim() || reading}
                     className="h-10 px-4 rounded-full bg-[#9061DA] disabled:bg-white/10 disabled:text-white/40 text-sm font-semibold flex items-center gap-2"
                     data-testid="cf-describe-send"
                  >
                     {reading && <Spinner size={14} />}
                     Fill in below
                  </button>
               </div>
            </form>
            {heard && (
               <div
                  className="-mt-2 flex flex-wrap items-center gap-1.5 text-xs"
                  data-testid="cf-understood"
               >
                  {heard.understood.length ? (
                     <>
                        <span className="text-[#9ca3af]">Understood</span>
                        {heard.understood.map((u) => (
                           <span
                              key={u}
                              className="h-6 px-2.5 rounded-full bg-[#A684FF]/15 text-[#DAC2FF] flex items-center"
                           >
                              {u}
                           </span>
                        ))}
                     </>
                  ) : (
                     <span className="text-[#9ca3af]">
                        Nothing matched your leads yet. Try a source, stage, tag
                        or pond name.
                     </span>
                  )}
                  {heard.unknown.length > 0 && (
                     <span
                        className="text-amber-300/90"
                        data-testid="cf-unknown"
                     >
                        Not found in your CRM: {heard.unknown.join(', ')}
                     </span>
                  )}
               </div>
            )}

            {heard?.ask && (
               <div
                  className="rounded-xl border border-[#9061DA]/40 bg-[#9061DA]/10 p-4 flex flex-col gap-3"
                  data-testid="cf-ask-kind"
               >
                  <div>
                     <div className="text-sm font-semibold">
                        {heard.ask.length > 1
                           ? 'Which one is this campaign?'
                           : 'New leads or cold leads?'}
                     </div>
                     <div className="text-xs text-[#9ca3af] mt-0.5">
                        {ONE_TYPE}
                     </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                     {KINDS.map((k) => (
                        <button
                           key={k}
                           type="button"
                           onClick={() => pickKind(k)}
                           className={cn(
                              'text-left rounded-lg border px-3 py-2.5 hover:border-[#DAC2FF] transition-colors',
                              heard.ask?.includes(k)
                                 ? 'border-[#9061DA] bg-[#9061DA]/20'
                                 : 'border-white/[0.12] bg-white/[0.03]'
                           )}
                           data-testid={`cf-ask-${k}`}
                        >
                           <span className="block text-[13px] font-semibold">
                              {
                                 CAMPAIGN_TYPE_OPTIONS.find((o) => o.id === k)
                                    ?.label
                              }
                           </span>
                           <span className="block text-xs text-[#9ca3af]">
                              {KIND_BLURB[k]}
                           </span>
                        </button>
                     ))}
                  </div>
               </div>
            )}

            {/* Sections, one at a time */}
            {showTypes && (
               <div
                  className="flex flex-col gap-2"
                  data-testid="cf-lead-types-section"
               >
                  <div className="text-[13px] font-semibold">
                     Looking to{' '}
                     <span className="font-normal text-[#818181]">
                        none picked = all leads
                     </span>
                  </div>
                  <div
                     className="flex flex-wrap gap-2"
                     data-testid="cf-lead-types"
                  >
                     {LEAD_TYPES.map((t) => {
                        const on = a.leadTypes.includes(t)
                        return (
                           <button
                              key={t}
                              type="button"
                              onClick={() =>
                                 setAudience((x) => ({
                                    ...x,
                                    leadTypes: toggle(x.leadTypes, t),
                                 }))
                              }
                              className={chip(on)}
                              data-testid={`cf-type-${t}`}
                              aria-pressed={on}
                           >
                              <Checkbox on={on} />
                              <span className="text-sm font-semibold">
                                 {
                                    {
                                       buyer: 'Buy',
                                       seller: 'Sell',
                                       rental: 'Rent',
                                    }[t]
                                 }
                              </span>
                              <span className="text-xs text-[#9ca3af]">
                                 {LEAD_TYPE_LABEL[t].toLowerCase()}
                              </span>
                           </button>
                        )
                     })}
                  </div>
               </div>
            )}

            {showActivity && (
               <div
                  className="flex flex-col gap-3 rounded-xl border border-white/[0.08] bg-white/[0.02] p-4"
                  data-testid="cf-activity"
               >
                  <div>
                     <div className="text-[13px] font-semibold">Trigger</div>
                     <div className="text-xs text-[#9ca3af]">
                        We call a lead right after they do one of these. "Any
                        event" means anything Follow Up Boss records.
                     </div>
                  </div>
                  <MultiSelect
                     label="When a lead"
                     data={EVENT_OPTIONS}
                     value={activity.events}
                     onChange={(next) => {
                        const added = next.find(
                           (e) => !activity.events.includes(e)
                        )
                        const rest = next.filter((e) => e !== 'all')
                        setActivity({
                           events:
                              added === 'all' || !rest.length ? ['all'] : rest,
                        })
                     }}
                     searchable
                     placeholder="Any event"
                     data-testid="cf-activity-events"
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                     <label
                        className="flex items-center gap-2 text-xs text-[#9ca3af]"
                        title="0 = only activity from now on"
                     >
                        Also include activity from the last
                        <input
                           type="number"
                           min={0}
                           max={365}
                           value={activity.lookbackDays ?? 0}
                           onChange={(e) =>
                              setActivity({
                                 lookbackDays: Number(e.target.value) || null,
                              })
                           }
                           className="w-16 h-9 rounded-lg bg-white/[0.06] border border-white/[0.12] px-2 text-sm text-white outline-none focus:border-[#9061DA]"
                           data-testid="cf-activity-lookback"
                        />
                        days
                     </label>
                     <MultiSelect
                        data={filterVocabularyNow().assigned_pond_id}
                        value={activity.pondIds}
                        onChange={(pondIds) => setActivity({ pondIds })}
                        placeholder="Or when they enter a pond"
                        searchable
                        data-testid="cf-activity-ponds"
                     />
                  </div>
               </div>
            )}

            {a.filters.length > 0 && (
               <div className="flex flex-col gap-2" data-testid="cf-filters">
                  <div className="text-[13px] font-semibold">
                     Narrow it down
                  </div>
                  {optionsSource ? (
                     <FilterOptionsContext.Provider value={optionsSource}>
                        {filterRows}
                     </FilterOptionsContext.Provider>
                  ) : (
                     filterRows
                  )}
               </div>
            )}

            {/* Add a filter manually: the dashboard's own field dropdown, one section at a time */}
            {menu.length > 0 && (
               <Select
                  data={menu.map((m) => ({ value: m.key, label: m.label }))}
                  value={null}
                  onChange={(v) => v && openSection(v as TSection)}
                  placeholder="Add a filter manually"
                  leftSection={<FlowIcon name="plus" size={14} />}
                  renderOption={({ option }) => (
                     <span className="flex items-center gap-2">
                        {option.value in ICONS_MAP ? (
                           ICONS_MAP[option.value as keyof typeof ICONS_MAP]
                        ) : (
                           <FlowIcon
                              name={option.value === 'types' ? 'users' : 'zap'}
                              size={16}
                           />
                        )}
                        {option.label}
                     </span>
                  )}
                  className="self-start w-full sm:w-[320px]"
                  comboboxProps={{ withinPortal: true }}
                  data-testid="cf-add-filter"
               />
            )}

            {/* The name, once something has been chosen */}
            {started && kind && (
               <div className="flex flex-col gap-1.5" data-testid="cf-name">
                  <label className="flex items-center gap-3 text-[13px] text-[#9ca3af]">
                     Campaign name
                     <input
                        value={a.name}
                        onChange={(e) => {
                           nameTouched.current = true
                           update((d) => ({
                              ...d,
                              audience: { ...d.audience, name: e.target.value },
                           }))
                        }}
                        placeholder={suggested || 'Name this campaign'}
                        className="flex-1 max-w-[460px] h-9 rounded-lg bg-white/[0.06] border border-white/[0.12] px-3 text-sm text-white outline-none focus:border-[#9061DA] placeholder:text-white/30"
                        data-testid="cf-name-input"
                     />
                  </label>
                  {suggested && suggested !== a.name && (
                     <button
                        type="button"
                        onClick={() => {
                           nameTouched.current = false
                           update((d) => ({
                              ...d,
                              audience: { ...d.audience, name: suggested },
                           }))
                        }}
                        className="self-start text-xs text-[#DAC2FF] hover:text-white"
                        data-testid="cf-name-suggest"
                     >
                        Suggested: {suggested}
                     </button>
                  )}
               </div>
            )}
         </div>
         <StepFooter
            hint="We skip anyone marked do-not-call or already closed."
            primary={
               missing
                  ? missing
                  : count
                    ? `Continue with ${count.toLocaleString()} leads`
                    : counting
                      ? 'Counting leads'
                      : kind === 'new-lead'
                        ? 'Continue · calls new leads as they arrive'
                        : 'No leads match yet'
            }
            /* A new-leads campaign may start empty: it calls every lead that arrives from now on. */
            disabled={
               !!missing ||
               (!count && kind !== 'new-lead') ||
               counting ||
               !a.name.trim()
            }
            busy={busy}
            busyLabel="Writing your playbook from the library…"
            onPrimary={onDone}
         />
      </>
   )
}
